import { useMutation, useQueryClient } from "react-query";
import addressService from "services/address";
import { IAddress } from "interfaces/address.interface";
import { useSettings } from "contexts/settings/settings.context";
import { useAuth } from "contexts/auth/auth.context";

export function useDeleteAddress() {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => addressService.delete(id),

    onMutate: async (id) => {
      await queryClient.cancelQueries("addresses");
      const prevAddresses = queryClient.getQueryData<IAddress[]>("addresses", {
        exact: false,
      });

      queryClient.setQueryData<IAddress[] | undefined>(
        ["addresses", isAuthenticated, user?.id],
        (old) => {
          if (!old) return prevAddresses;
          return old
            .flatMap((addressList) => addressList)
            .filter((oldAddress) => oldAddress.id !== id);
        },
      );
      return { prevAddresses };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData("addresses", context?.prevAddresses);
    },
  });
}

export function useChangeAddress() {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const { updateAddress, updateLocation } = useSettings();

  return useMutation({
    mutationFn: (id: number) => addressService.setDefault(id),

    onMutate: async (id) => {
      await queryClient.cancelQueries("addresses");
      const prevAddresses = queryClient.getQueryData<IAddress[]>("addresses", {
        exact: false,
      });

      queryClient.setQueryData<IAddress[] | undefined>(
        ["addresses", isAuthenticated, user?.id],
        (old) => {
          if (!old) return prevAddresses;
          return old
            .flatMap((addressList) => addressList)
            .map((oldAddress) => {
              if (oldAddress.id === id) {
                updateAddress(oldAddress.address?.address);
                updateLocation(
                  `${oldAddress.location.at(0)},${oldAddress.location.at(1)}`,
                );
                return { ...oldAddress, active: true };
              }
              return { ...oldAddress, active: false };
            });
        },
      );
    },
  });
}
